import { useGT } from 'gt-react';
import type { LayoutChange } from './layoutChange';
import type { ComponentRegistry } from './registry';

/**
 * A layout change as a step of the edit history: labelled with the component
 * it touched, and grouped so a drag or a burst of typing on one component is
 * one step.
 */
export function useLayoutHistoryStep(registry: ComponentRegistry) {
  const gt = useGT();
  return (change: LayoutChange): { label: string; group?: string } => {
    if (change.kind === 'layout')
      return {
        label: gt('Changed the layout', {
          context: 'report edit history step',
        }),
      };
    const { component } = change;
    const label = componentStepLabel(
      gt,
      change.kind,
      componentName(registry, component),
    );
    return change.kind === 'add' || change.kind === 'remove'
      ? { label }
      : { label, group: `${change.kind}:${component.id}` };
  };
}

type ComponentChange = Exclude<LayoutChange, { kind: 'layout' }>;

function componentName(
  registry: ComponentRegistry,
  component: ComponentChange['component'],
): string {
  return (
    component.title?.trim() || registry[component.type]?.label || component.type
  );
}

function componentStepLabel(
  gt: ReturnType<typeof useGT>,
  kind: ComponentChange['kind'],
  name: string,
): string {
  switch (kind) {
    case 'add':
      return gt('Added {name}', {
        name,
        context: 'report edit history step',
      });
    case 'remove':
      return gt('Removed {name}', {
        name,
        context: 'report edit history step',
      });
    case 'move':
      return gt('Moved {name}', {
        name,
        context: 'report edit history step',
      });
    case 'resize':
      return gt('Resized {name}', {
        name,
        context: 'report edit history step',
      });
    case 'edit':
      return gt('Edited {name}', {
        name,
        context: 'report edit history step',
      });
  }
}
